import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, CircleSlash, FileText, PlayCircle, ShieldCheck, UserCheck } from "lucide-react";
import { MaskedLine, EASE } from "./Reveal";
import { useSite } from "../../context/SiteContext";

const BADGES = [
  { icon: ShieldCheck, label: "HIPAA Compliant", pos: "-left-4 top-8 lg:-left-10", delay: 0 },
  { icon: UserCheck, label: "Expert Trained Scribes", pos: "-right-3 top-24 lg:-right-8", delay: 1.2 },
  { icon: FileText, label: "Live Documentation", pos: "-left-5 bottom-24 lg:-left-12", delay: 2.1 },
  { icon: CircleSlash, label: "No AI Hallucinations", pos: "right-2 -bottom-5 lg:right-6", delay: 0.6 },
];

const Hero = () => {
  const { openBooking, scrollToId } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, 48]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 20]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-gradient-to-br from-white via-brand-sky-soft/60 to-white dark:from-brand-ink dark:via-[#0A1030] dark:to-brand-ink grain">
      <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-brand-sky/15 blur-3xl" />
      <div className="absolute top-1/2 -left-40 w-[420px] h-[420px] rounded-full bg-brand-navy/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-36 pb-20 md:pt-44 md:pb-28 grid lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center">
        <motion.div style={{ y: yText, willChange: "transform" }}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 mb-8"
            data-testid="hero-pill"
          >
            <span className="w-2 h-2 rounded-full bg-brand-sky animate-pulse" />
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-brand-navy dark:text-brand-sky">
              Virtual Live Human Medical Scribing
            </span>
          </motion.div>
          <h1 className="font-display font-bold tracking-tighter leading-[1.04] text-slate-900 dark:text-white text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <MaskedLine delay={0.15}>Spend More Time</MaskedLine>
            <MaskedLine delay={0.28}>With Patients.</MaskedLine>
            <MaskedLine delay={0.41} className="text-brand-gradient">We'll Handle the</MaskedLine>
            <MaskedLine delay={0.54} className="text-brand-gradient">Documentation.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-slate-600 dark:text-slate-300"
            data-testid="hero-subheadline"
          >
            Virtual Live Human Medical Scribes — expert, trained scribes documenting your patient encounters in real time,
            directly within your EHR. Reducing physician burnout, improving documentation quality, and supporting
            better coding opportunities. Not AI. Not in-person. One service, done right.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-call-button"
              onClick={openBooking}
              className="group inline-flex items-center gap-2.5 rounded-full bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink font-bold px-8 py-4 text-sm hover:shadow-[0_20px_45px_-14px_rgba(42,49,146,0.55)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
            >
              Book a Discovery Call
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-how-it-works-button"
              onClick={() => scrollToId("how-it-works")}
              className="group inline-flex items-center gap-2.5 rounded-full border border-slate-300 dark:border-white/15 text-slate-800 dark:text-white font-bold px-8 py-4 text-sm hover:border-brand-sky hover:text-brand-navy dark:hover:text-brand-sky transition-colors duration-300"
            >
              <PlayCircle size={17} />
              See How It Works
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yArt, willChange: "transform" }} className="relative hidden md:block" data-testid="hero-illustration">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
            className="relative"
          >
            <svg className="absolute -inset-8 w-[calc(100%+64px)] h-[calc(100%+64px)] text-brand-sky/50" viewBox="0 0 500 420" fill="none" aria-hidden="true">
              <motion.path
                d="M60 340 C 120 200, 200 120, 440 90"
                stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 8" className="animate-dash-flow"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 1.2, ease: "easeInOut" }}
              />
              <motion.path
                d="M80 60 C 180 140, 300 260, 430 330"
                stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 8" className="animate-dash-flow"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 1.6, ease: "easeInOut" }}
              />
            </svg>
            <div className="relative glass rounded-3xl shadow-[0_40px_90px_-30px_rgba(42,49,146,0.35)] p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-white/20" />
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-white/20" />
                  <span className="w-2.5 h-2.5 rounded-full bg-brand-sky" />
                </div>
                <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">EHR — Encounter #4821</span>
              </div>
              <div className="grid grid-cols-[1fr_110px] gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-navy to-brand-sky flex items-center justify-center text-white text-xs font-bold">DR</div>
                    <div>
                      <p className="text-xs font-bold text-slate-800 dark:text-white">Dr. Patel — with patient</p>
                      <p className="text-[10px] text-slate-400">Exam Room 2 · 10:42 AM</p>
                    </div>
                    <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold px-2.5 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
                    </span>
                  </div>
                  {["92%", "74%", "58%"].map((w, i) => (
                    <motion.div key={w} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.1 + i * 0.25, duration: 0.7, ease: EASE }} style={{ width: w }} className="origin-left h-2.5 rounded-full bg-slate-200 dark:bg-white/10" />
                  ))}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2, duration: 0.6, ease: EASE }}
                    className="rounded-2xl border border-brand-sky/40 bg-brand-sky/10 p-3.5 flex items-center gap-3"
                  >
                    <BadgeCheck size={18} className="text-brand-navy dark:text-brand-sky shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-brand-navy dark:text-brand-sky">Assessment &amp; Plan — ready for review</p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">Documented live · Signed 10:58 AM</p>
                    </div>
                  </motion.div>
                </div>
                <motion.div
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.4, duration: 0.7, ease: EASE }}
                  className="rounded-2xl bg-gradient-to-b from-brand-navy to-brand-navy-deep p-3 flex flex-col items-center justify-center text-center gap-2"
                >
                  <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                    <UserCheck size={18} className="text-brand-sky" />
                  </div>
                  <p className="text-[10px] font-bold text-white leading-tight">Remote Scribe</p>
                  <p className="text-[9px] text-brand-sky/80">Connected securely</p>
                </motion.div>
              </div>
            </div>
            {BADGES.map((b, i) => (
              <motion.div
                key={b.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.1 + i * 0.15, duration: 0.45, ease: EASE }}
                className={`absolute ${b.pos} animate-float-slow`}
                style={{ animationDelay: `${b.delay}s` }}
                data-testid={`hero-badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="rounded-full bg-white/95 dark:bg-[#0D1434]/95 border border-white/60 dark:border-white/10 pl-2.5 pr-4 py-2 flex items-center gap-2 shadow-lg shadow-brand-navy/10">
                  <b.icon size={14} className="text-brand-navy dark:text-brand-sky" />
                  <span className="text-[11px] font-bold text-slate-800 dark:text-white whitespace-nowrap">{b.label}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
